import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import patientService from '../../../../services/patient.service';
import SubHeader from '../../../layout/components/SubHeader';
import PatientInfoCard from './PatientInfoCard';
import PatientTabs from './PatientTabs';
import PatientDetailSkeleton from './subparts/PatientDetailSkeleton';
import '../PatientFiles.css';

function Patient() {
    const [loading, setLoading] = useState(true);
    const [patientInfo, setPatientInfo] = useState(null);
    const [commPrefs, setCommPrefs] = useState(null);

    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const { patientId } = useParams();

    const getPatientInfo = useCallback(async () => {
        if (!clinicId || !patientId) {
            return;
        }

        setLoading(true);

        try {
            const [patientRes, commPrefsRes] = await Promise.all([
                patientService.getPatientById(clinicId, patientId),
                patientService.getCommunicationPreferences(clinicId, patientId),
            ]);
            setPatientInfo(patientRes.data);
            setCommPrefs(commPrefsRes.data);
        } catch (error) {
            console.error('Error fetching patient:', error);
            setPatientInfo(null);
            setCommPrefs(null);
        } finally {
            setLoading(false);
        }
    }, [clinicId, patientId]);

    useEffect(() => {
        getPatientInfo();
    }, [getPatientInfo]);

    const patientName = useMemo(() => {
        if (!patientInfo) {
            return 'Patient Details';
        }

        return [patientInfo.firstName, patientInfo.lastName].filter(Boolean).join(' ') || 'Patient Details';
    }, [patientInfo]);

    const description = useMemo(() => {
        if (!patientInfo) {
            return 'View and manage patient profile, appointments, and communication settings';
        }

        const parts = [];

        if (patientInfo.preferredName) {
            parts.push(`Preferred name: ${patientInfo.preferredName}`);
        }

        if (patientInfo.pmsId) {
            parts.push(`PMS ID: ${patientInfo.pmsId}`);
        }

        parts.push(patientInfo.isActive ? 'Active patient' : 'Inactive patient');

        return parts.join(' · ');
    }, [patientInfo]);

    return (
        <div className="ecx-page patient-files-page patient-detail-page ecx-page--full-width">
            <SubHeader
                title={patientName}
                description={description}
                icon="bi-person-vcard"
                containerClass="w-100 px-4"
            >
                <Link to="/patient-files" className="btn btn-light-primary btn-sm">
                    <i className="bi bi-arrow-left me-1" />
                    Back to Patient Files
                </Link>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel patient-detail-panel">
                    {loading ? (
                        <PatientDetailSkeleton />
                    ) : patientInfo ? (
                        <>
                            <div className="ecx-page-panel__header patient-detail-summary-wrap border-top-0">
                                <PatientInfoCard patient={patientInfo} commPrefs={commPrefs} />
                            </div>

                            <div className="ecx-page-panel__body ecx-page-scroll patient-detail-tabs-wrap">
                                <PatientTabs
                                    patient={patientInfo}
                                    commPrefs={commPrefs}
                                    onPatientUpdated={setPatientInfo}
                                    onCommPrefsUpdated={setCommPrefs}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="patient-detail-empty ecx-page-empty">
                            <span className="ecx-page-empty__icon">
                                <i className="bi bi-person-x" />
                            </span>
                            <h5 className="mb-2 text-dark">Patient not found</h5>
                            <p className="mb-3">This patient record could not be loaded or may no longer exist.</p>
                            <Link to="/patient-files" className="btn btn-light-primary">
                                <i className="bi bi-arrow-left me-2" />
                                Return to Patient Files
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Patient;
